import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner, Alert, Accordion } from 'react-bootstrap';
import { getBookmarks, addBookmark, removeBookmark, GUEST_LOGIN } from './CabinetPage';
import OrderModal from './OrderModal';
import CoverArt from './CoverArt';
import { API_URL } from '../config';
import { readerFetch } from '../apiAuth';
import './BookFullView.css';
import { t } from '../i18n';
import { safeExtUrl as _safeExtUrl } from '../safeUrl';
import { copyText } from '../clipboard';



// ── Коди мов ISO 639-2 → читабельна назва ────────────────
const LANG_NAMES = {
  ukr: 'Українська',   rus: 'Російська',    eng: 'Англійська',
  deu: 'Німецька',     fra: 'Французька',   pol: 'Польська',
  bel: 'Білоруська',   ita: 'Італійська',   spa: 'Іспанська',
  lat: 'Латинська',    grc: 'Давньогрецька',jpn: 'Японська',
  zho: 'Китайська',    chi: 'Китайська',    ara: 'Арабська',
  heb: 'Іврит',        tur: 'Турецька',     kaz: 'Казахська',
};
function langName(code) {
  if (!code) return '';
  return LANG_NAMES[code.trim().toLowerCase()] || code;
}
// Очищення HTML-тегів з лейбла повного тексту (IRBIS передає <img>-теги)
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

function getFtIcon(label) {
  const l = (label || '').toLowerCase();
  if (/scopus/.test(l))                         return 'fa-database';
  if (/wos|web of science/.test(l))             return 'fa-database';
  if (/index copernicus/.test(l))               return 'fa-database';
  if (/google scholar/.test(l))                 return 'fa-graduation-cap';
  if (/репозитор/.test(l))                      return 'fa-box-archive';
  if (/відкрит/.test(l))                        return 'fa-unlock';
  if (/веб-сайт|нбув/.test(l))                  return 'fa-globe';
  if (/цифрова копія/.test(l))                  return 'fa-copy';
  if (/окремий номер/.test(l))                  return 'fa-newspaper';
  return 'fa-arrow-up-right-from-square';
}


// ── Рядок інфо ───────────────────────────────────────────
function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="bfv-info-row">
      <span className="bfv-info-label">
        {icon && <i className={icon} />}
        {label}
      </span>
      <span className="bfv-info-value">{value}</span>
    </div>
  );
}

function joinZones(arr) {
  let out = '';
  for (const z of arr) {
    if (!z) continue;
    if (!out) { out = z; continue; }
    out += (out.endsWith('.') ? ' \u2014 ' : '. \u2014 ') + z;
  }
  return out && !out.endsWith('.') ? out + '.' : out;
}


// ═══════════════════════════════════════════════════════════
// ПОВНИЙ БІБЛІОГРАФІЧНИЙ ОПИС  (ISBD / UNIMARC · RUSMARC)
// Зони: 1-Назва/Відп · 2-Видання · 4-Вихідні дані ·
//       5-Фізичний опис · 6-Серія · 8-ISBN
// Аналітика (статті): Назва / Відп. // Джерело. — Рік. — № . — С.
// ═══════════════════════════════════════════════════════════
function IsbdDescription({ record, db, onRubricSearch }) {           // db — для майбутнього використання
  const SEP = '. \u2014 ';                           // '. — ' — розділювач зон ISBD

  const isArticle = !!(record.analytics_source);

  // ── Зона 1: Назва і відомості про відповідальність ──────
  // 200^a : 200^e / 200^f ; 200^g = 200^d [200^b]
  let z1 = record.title || '';
  // Нумерація серіального видання (207^a): "Т. 1. № 1"
  if (record.ser_designation) {
    z1 += `. ${record.ser_designation}`;
  } else {
    if (record.vol_title)  z1 += /^\d/.test(record.vol_title) ? `. Т.\u00A0${record.vol_title}` : `. ${record.vol_title}`;
    if (record.issue_num)  z1 += `. №\u00A0${record.issue_num}`;
  }
  if (record.material_type) z1 += ` [${record.material_type}]`;
  if (record.parallel_title) z1 += ` = ${record.parallel_title}`;
  if (record.subtitle)       z1 += ` : ${record.subtitle}`;
  const respParts = [record.responsibility, record.other_responsibility].filter(Boolean);
  if (respParts.length)      z1 += ` / ${respParts.join(' ; ')}`;

  let titleLine;

  if (isArticle) {
    // ── Аналітичний опис (статті KST) ────────────────────
    // Назва / Відп. // Джерело. — Рік. — Т. N. — № N. — С. N-N.
    const src = record.analytics_source;
    titleLine = z1;
    if (src.title) titleLine += ` // ${src.title}`;

    const yr = src.year || record.year || '';
    if (yr)        titleLine += `${SEP}${yr}`;
    if (src.vol) titleLine += `${SEP}Т.\u00A0${src.vol}`;
    if (src.issue) titleLine += `${SEP}\u2116\u00A0${src.issue}`; // №

    const pages = (src.pages || record.pages || '').trim();
    if (pages) {
      // Якщо сторінки вже мають "С." або "Р." — не додавати
      const pageStr = /^[СсCcPpРр]\.?\s/.test(pages) ? pages : `С.\u00A0${pages}`;
      titleLine += `${SEP}${pageStr}`;
    }
    titleLine += '.';

  } else {
    // ── Монографічний опис (книги KV, автореферати KAD) ──
    const zones = [z1]; // зона 1 — без крапки в кінці (joiner додає)

// Зона 2: Відомості про видання (205^a)
if (record.edition) zones.push(record.edition);

// Зона 3: Специфічне позначення матеріалу (230^a) — для е-ресурсів
if (record.electronic_type) zones.push(record.electronic_type);

// Зона 4: Вихідні дані

    // Зона 4: Вихідні дані (210^a, 210^c, 210^d)
    const pubs = (record.publishers_full?.length > 0)
      ? record.publishers_full
      : (record.place || record.publisher)
        ? [{ place: record.place || '', publisher: record.publisher || '', year: record.year || '' }]
        : [];

    if (pubs.length > 0) {
      const pubStr = pubs
      .map(p => {
        let s = p.place || '';
        const pub = p.publisher || '[б.\u00A0в.]';
        s += (s ? '\u00A0: ' : '') + pub;
        return s;
      })
        .filter(Boolean)
        .join(' ; ');
      const yr = (pubs.find(p => p.year) || {}).year || record.year || '';
      let z4 = pubStr;
      if (yr) z4 += (z4 ? ',\u00A0' : '') + yr;
      if (pubs[0]?.printing_place) z4 += ` (${pubs[0].printing_place})`;
      if (z4) zones.push(z4);
    }

    // Періодичність (110^d — розшифровка довідником RKSP.MNU на бекенді);
    // якщо код не розпізнано — резервний варіант із сирим числом (110^x)
    if (record.periodicity_label) {
      zones.push(record.periodicity_label);
    } else if (record.periodicity_per_year) {
      zones.push(`Виходить: ${record.periodicity_per_year} вип. на рік`);
    }

    // Позначення конкретного номера періодики (934+936, сесія 2026-08-07/2):
    // "1985р. № 1" — окремим рядком, як у старому вебі. Записи-номери самі
    // не мають цих даних у вигляді ISBD-зони — journal_year/issue_label
    // це вже готові значення полів 934/936
    if (record.journal_year && record.issue_label) {
      zones.push(`${record.journal_year}р. № ${record.issue_label}`);
    }

    // Зона 5: Фізичний опис (215^a, 215^c, 215^d, 215^e)
    // Формат: кількість с. : іл. ; розмір см + додаток
    let z5 = '';
    if (record.pages) {
      const p = record.pages.trim();
      // Додаємо 'с.' якщо значення закінчується на цифру
      z5 = /\d\s*$/.test(p) ? `${p} с.` : p;
    }
    if (record.phys_ill)    z5 += (z5 ? ' : '  : '') + record.phys_ill;
    if (record.phys_size)   z5 += (z5 ? ' ; '  : '') + `${record.phys_size}\u00A0см`;
    if (record.phys_accomp) z5 += (z5 ? ' + '  : '') + record.phys_accomp;
    if (z5) zones.push(z5);

    // Зона 6: Серія (225^a, 225^v)
    if (record.series) {
      let ser = `(${record.series}`;
      if (record.series_vol) ser += ` ; ${record.series_vol}`;
      ser += ')';
      zones.push(ser);
    }

    // Зона 8: ISBN (поле 10^a)
    const isbns = (record.all_isbns?.length > 0)
      ? record.all_isbns
      : (record.isbn ? [record.isbn] : []);
      isbns.forEach(isbn => zones.push(`ISBN\u00A0${isbn}`));
      if (record.issn) zones.push(`ISSN\u00A0${record.issn}`);

    titleLine = joinZones(zones);
  }

  // ── Допоміжні дані ──────────────────────────────────────
  const callParts    = [record.call_number_class, record.author_sign].filter(Boolean);
  const rubrics      = record.rubrics       || [];
  const addedEntries = record.added_entries || [];
  const hasContent   = record.title || rubrics.length || addedEntries.length;
  if (!hasContent) return null;

  return (
    <div className="bfv-isbd-card">
      {callParts.length > 0 && (
        <div className="bfv-isbd-callno">
          {callParts.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
      <div className="bfv-isbd-body">
      {record.main_author && !['01','02','04'].includes(record.vyd_type) && (
  <div className="bfv-isbd-mainentry">
    {record.main_author.replace(/\.+$/, '')}.
  </div>
)}
        <div className="bfv-isbd-titleline">{titleLine}</div>
        {record.shifr && (
          <div className="bfv-isbd-fieldline">
            <span className="bfv-isbd-label">Шифр:</span> {record.shifr}
          </div>
        )}
        {(record.continues?.length > 0 || record.continued_by?.length > 0) && (
          <div className="bfv-isbd-fieldline bfv-isbd-continues">
            <span className="bfv-isbd-label">Відомості про зміни видання:</span>{' '}
            {[...(record.continues || []), ...(record.continued_by || [])].map((c, i, arr) => (
              <span key={i}>
                {c.year ? `З ${c.year} р. ` : ''}
                {c.label || 'пов’язане видання'}{' '}
                {c.title}
                {c.shifr ? ` (Шифр ${c.shifr})` : ''}
                {i < arr.length - 1 ? '; ' : ''}
              </span>
            ))}
          </div>
        )}
        {(record.all_udc?.length > 0 || record.udc) && (
          <div className="bfv-isbd-fieldline">
            <span className="bfv-isbd-label">УДК</span>
            {(record.all_udc?.length > 0 ? record.all_udc : [record.udc]).map((u, i) => (
              <span key={i} className="bfv-isbd-udc-val">{u}</span>
            ))}
          </div>
        )}
{rubrics.length > 0 && (
  <div className="bfv-isbd-fieldline">
    <span className="bfv-isbd-label">Рубрики:</span>{' '}
    {rubrics.map((r, i) => (
      <button
        key={i}
        className={`bfv-rubric-tag${onRubricSearch ? ' bfv-rubric-clickable' : ''}`}
        onClick={() => onRubricSearch && onRubricSearch(r)}
        title={onRubricSearch ? `Знайти за рубрикою: ${r}` : undefined}>
        {r}
      </button>
    ))}
  </div>
)}
        {addedEntries.length > 0 && (
          <div className="bfv-isbd-fieldline">
            <span className="bfv-isbd-label">Дод.&nbsp;точки&nbsp;доступу:</span>{' '}
            {addedEntries.map((e, i) => (
              <span key={i}>
                {e.name}{e.role && <> \{e.role}\</>}
                {i < addedEntries.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


const UNIMARC_LABELS = {
  // Блок 0 — Ідентифікація
  10:  'ISBN',
  11:  'ISSN',
  13:  'ISMN',
  19:  'DOI',
  // Блок 1 — Кодована інформація
  60:  'Службове поле',
  99:  'Службове поле',
  100: 'Дані загальної обробки',
  101: 'Мова документу',
  102: 'Країна публікації',
  105: 'Кодові дані: монографія',
  110: 'Кодові дані: серіальне видання',
  115: 'Кодові дані: відеозапис',
  116: 'Кодові дані: графічні матеріали',
  120: 'Кодові дані: картографічні матеріали',
  125: 'Кодові дані: нотні матеріали',
  126: 'Кодові дані: звуковий запис',
  135: 'Кодові дані: електронні ресурси',
  // Блок 2 — Описова інформація
  181: 'Ознаки типу матеріалу',
  182: 'Ознаки типу запису',
  183: 'Ознаки типу матеріалу (додаткові)',
  200: 'Назва та відомості про відповідальність',
  203: 'Вид матеріалу',
  204: 'Відомості про зміст',
  205: 'Відомості про видання',
  206: 'Специфічні відомості: картогр. матеріали',
  207: 'Нумерація серіального видання',
  208: 'Специфічні відомості: нотні видання',
  210: 'Місце та час видання',
  215: 'Фізичний опис',
  225: 'Серія',
  230: 'Специфічне позначення матеріалу',
  // Блок 3 — Примітки
  300: 'Загальна примітка',
  304: 'Примітка про заголовок',
  305: 'Примітка про видання',
  307: 'Примітка про фізичний опис',
  310: 'Примітка про регулярність виходу',
  314: 'Примітка про інтелектуальну відповідальність',
  320: 'Примітки до бібліографії та покажчиків',
  321: 'Примітка про короткий зміст',
  327: 'Зміст',
  328: 'Примітка про дисертацію',
  330: 'Анотація або резюме',
  331: 'Анотація',
  333: 'Примітка про аудиторію',
  336: 'Вид публікації',
  337: 'Відомості про е-ресурс та носій',
  // Блок 4 — Зв'язані видання
  410: 'Серія',
  411: 'Підсерія',
  421: 'Додаток',
  430: 'Продовжує',
  440: 'Продовжений у',
  451: 'Інше видання на тому ж носії',
  452: 'Інше видання на іншому носії',
  453: 'Перекладено як',
  454: 'Перекладено з',
  461: 'Входить до комплекту',
  463: 'Аналітика: частина одиниці зберігання',
  464: 'Складова частина',
  // Блок 5 — Варіанти назви
  500: 'Уніфікований заголовок',
  503: 'Уніфікований заголовок. Аналітика',
  510: 'Паралельна назва',
  512: 'Варіант назви на обкладинці',
  513: 'Скорочена назва',
  514: 'Розширена назва',
  515: 'Назва на корінці',
  516: 'Назва частини або розділу',
  517: 'Інші варіанти назви',
  // Блок 6 — Предметний аналіз
  600: 'Предметна рубрика — ім\'я особи',
  601: 'Предметна рубрика — найменування організації',
  602: 'Предметна рубрика — родина',
  604: 'Предметна рубрика — особа / твір',
  605: 'Предметна рубрика — назва',
  606: 'Предметна рубрика — тематична',
  607: 'Предметна рубрика — географічна назва',
  608: 'Предметна рубрика — форма / жанр',
  610: 'Ненормований предметний термін',
  675: 'УДК — Універсальна десяткова класифікація',
  676: 'ДКД — Десяткова класифікація Дьюї',
  680: 'ББК',
  686: 'РУБ — рубрика систематична',
  // Блок 7 — Інтелектуальна відповідальність
  700: 'Автор (основний запис)',
  701: 'Автор (альтернативний запис)',
  702: 'Вторинна відповідальність (особа)',
  710: 'Колектив (основний запис)',
  711: 'Колектив (альтернативний запис)',
  712: 'Вторинна відповідальність (організація)',
  720: 'Ім\'я (незадане)',
  // Блок 8 — Міжнародне використання
  801: 'Джерело походження запису',
  856: 'Електронне місцезнаходження та доступ',
  // ІРБІС-специфічні поля
  391: 'Автограф (підписаний примірник)',
  900: 'Тип документу',
  903: 'Шифр замовлення',
  905: 'Службові відомості',
  906: 'Систематичний шифр',
  907: 'Дата каталогізації',
  908: 'Авторський знак',
  910: 'Відомості про примірники',
  919: 'Мова та код доступу',
  920: 'Маркер виду запису',
  923: 'Нумерація випуску',
  930: 'Поновлення',
  948: 'Метадані',
  951: 'Повний текст (зовнішній)',
  953: 'Обкладинка / фото',
  955: 'Повний текст (внутрішній)',
  966: 'Службові дані',
  999: 'Лічильник книговидач',
};
const UNIMARC_HIDDEN = new Set([60, 99, 140, 141, 619, 686, 902, 905, 907, 910, 920, 932, 940, 941, 953, 955, 966, 967, 988]);

function UnimarcView({ fields, loading, error }) {
  if (loading) return (
    <div className="bfv-marc-loading">
      <i className="fa-solid fa-spinner fa-spin me-2" />
      Завантаження...
    </div>
  );
  if (error) return (
    <div className="bfv-marc-loading" style={{ color: 'var(--color-danger)' }}>
      <i className="fa-solid fa-triangle-exclamation me-2" />
      {error}
    </div>
  );
  if (!fields) return null;
  if (fields.length === 0) return <div className="bfv-marc-loading">Немає даних UNIMARC</div>;

  const groups = [];
  fields.forEach(f => {
    const last = groups[groups.length - 1];
    if (last && last.tag === f.tag) {
      last.rows.push(f);
    } else {
      groups.push({ tag: f.tag, rows: [f] });
    }
  });

  return (
    <div className="bfv-marc-table">
{groups.filter(g => !UNIMARC_HIDDEN.has(g.tag)).map((g, gi) => {
        const label = UNIMARC_LABELS[g.tag];
        const tagStr = g.tag > 9999 ? '---' : String(g.tag).padStart(3, '0');
        return (
          <div key={gi} className="bfv-marc-group">
            <div className="bfv-marc-group-header">
              <span className="bfv-marc-tag">{tagStr}</span>
              <span className="bfv-marc-ind">##</span>
              {label && <span className="bfv-marc-label">— {label}</span>}
            </div>
            {g.rows.map((f, fi) => (
              <div key={fi} className="bfv-marc-row">
                {f.value && <span className="bfv-marc-subval">{f.value}</span>}
                {f.subfields.filter(s => !(g.tag === 951 && s.code === 't')).map((s, j) => (
                  <span key={j} className="bfv-marc-sub">
                    <span className="bfv-marc-subcode">${s.code}</span>
                    <span className="bfv-marc-subval">{s.value}</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
// ── Кнопка «E-mail» з меню вибору поштового сервісу. Голий mailto: на ПК без
// налаштованої поштової програми (більшість користується поштою в браузері)
// не робить нічого — тому Gmail/Outlook відкриваються як веб-лист у новій вкладці ──
function EmailShareMenu({ lang, shareUrl, shareTitle, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const su = encodeURIComponent(shareTitle);
  const body = encodeURIComponent(shareUrl);
  const items = [
    { key: 'gmail', icon: 'fa-brands fa-google', label: 'Gmail', href: `https://mail.google.com/mail/?view=cm&fs=1&su=${su}&body=${body}`, blank: true },
    { key: 'outlook', icon: 'fa-brands fa-microsoft', label: 'Outlook', href: `https://outlook.live.com/mail/0/deeplink/compose?subject=${su}&body=${body}`, blank: true },
    { key: 'app', icon: 'fa-solid fa-envelope-open-text', label: t(lang, 'book_share_email_app'), href: `mailto:?subject=${su}&body=${body}`, blank: false },
  ];
  return (
    <div className="bfv-share-email-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`bfv-share-btn bfv-share-btn--email${open ? ' bfv-share-btn--email-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t(lang, 'book_share_email_title')}
        title={t(lang, 'book_share_email_title')}
        onClick={() => setOpen(v => !v)}
      ><i className="fa-solid fa-envelope" /></button>
      {open && (
        <div className="bfv-share-email-menu" role="menu">
          {items.map(it => (
            <a
              key={it.key}
              role="menuitem"
              href={it.href}
              className={`bfv-share-email-item bfv-share-email-item--${it.key}`}
              {...(it.blank ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={() => { setOpen(false); onPick(); }}
            >
              <i className={`${it.icon} bfv-share-email-item-icon`} />
              <span className="bfv-share-email-item-label">{it.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Рядок «Назад + поширення» — однаковий вміст рендериться двічі
// (мобільна й десктопна версії, розрізняються лише класом-обгорткою для CSS) ──
function BackShareRow({ wrapClass, onBack, backLabel, lang, shareUrl, shareTitle, copied, copyFailed, onCopyLink, onShare }) {
  const share = channel => { if (onShare) onShare(channel); };
  const copyBtnClass = `bfv-share-btn bfv-share-btn--copy${copied ? ' bfv-share-btn--copied' : ''}${copyFailed ? ' bfv-share-btn--copy-failed' : ''}`;
  const copyIcon = copyFailed ? 'fa-solid fa-triangle-exclamation' : (copied ? 'fa-solid fa-check' : 'fa-solid fa-copy');
  return (
    <div className={`bfv-back-share-row ${wrapClass}`}>
      <button className="bfv-back-btn" onClick={onBack}>
        <i className="fa-solid fa-arrow-left me-1" />
        {backLabel || t(lang, 'back_results')}
      </button>
      <div className="bfv-share-btns">
        <a href={`viber://forward?text=${encodeURIComponent(shareUrl)}`} className="bfv-share-btn bfv-share-btn--viber" target="_blank" rel="noreferrer" onClick={() => share('viber')}><i className="fa-brands fa-viber" /></a>
        <a href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`} className="bfv-share-btn bfv-share-btn--telegram" target="_blank" rel="noreferrer" onClick={() => share('telegram')}><i className="fa-brands fa-telegram" /></a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} className="bfv-share-btn bfv-share-btn--facebook" target="_blank" rel="noreferrer" onClick={() => share('facebook')}><i className="fa-brands fa-facebook-f" /></a>
        <a href={`fb-messenger://share/?link=${encodeURIComponent(shareUrl)}`} className="bfv-share-btn bfv-share-btn--messenger" target="_blank" rel="noreferrer" onClick={() => share('messenger')}><i className="fa-brands fa-facebook-messenger" /></a>
        <EmailShareMenu lang={lang} shareUrl={shareUrl} shareTitle={shareTitle} onPick={() => share('email')} />
        <button
          className={copyBtnClass}
          title={copyFailed ? t(lang, 'book_share_copy_failed') : undefined}
          onClick={() => { onCopyLink(); share('copy'); }}
        ><i className={copyIcon} /></button>
      </div>
    </div>
  );
}

// ── Кнопки «Увійти» / «Зареєструватися» в гостьових плашках — той самий
// блок рендериться і під кнопкою замовлення, і під внутрішнім повним текстом ──
function GuestAuthButtons({ onGoLogin, lang }) {
  if (!onGoLogin) return null;
  return (
    <span className="bfv-guest-auth-btns">
      <button type="button" className="bfv-guest-auth-btn bfv-guest-auth-btn--login"
        onClick={() => onGoLogin('login')}>
        {t(lang, 'book_go_login')}
      </button>
      <button type="button" className="bfv-guest-auth-btn bfv-guest-auth-btn--register"
        onClick={() => onGoLogin('register')}>
        {t(lang, 'book_go_register')}
      </button>
    </span>
  );
}

// ── Рядок міток УДК у таблиці «Основна інформація» — однаковий і для статей,
// і для книг/авторефератів, розрізнялись лише сусіднім контекстом ──
function UdcBadgeRow({ record, lang, onUdcSearch }) {
  if (!(record.all_udc?.length > 0 || record.udc)) return null;
  return (
    <div className="bfv-info-row">
      <span className="bfv-info-label">
        <i className="fa-solid fa-sitemap" />
        {t(lang, 'book_udc')}
      </span>
      <span className="bfv-info-value" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(record.all_udc?.length > 0 ? record.all_udc : [record.udc]).map((u, i) => (
          <button
            key={i}
            className={`bfv-author-pill${onUdcSearch ? ' bfv-author-pill--clickable' : ''}`}
            onClick={() => onUdcSearch && onUdcSearch(u)}
            title={onUdcSearch ? `${t(lang, 'udc_search_title')} ${u}` : undefined}
          >{u}{onUdcSearch && <i className="fa-solid fa-magnifying-glass ms-1" style={{ fontSize: 9, opacity: 0.55 }} />}</button>
        ))}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// BOOK FULL VIEW
// ═══════════════════════════════════════════════════════════
function BookFullView({ mfn, db, user, onBack, userMode, onKeywordSearch, onAuthorSearch, onUdcSearch, backLabel, issuedBooks, activeOrders, onOrderPlaced, lang = 'uk', trackEvent, onFulltextOpen, onGoLogin, onOpenJournalSummary, onOpenCabinetSection }) {
  const [record,           setRecord]           = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState('');
  const [bookmarked,       setBookmarked]       = useState(false);
  const [coverError,       setCoverError]       = useState(false);
  const [annotOpen,        setAnnotOpen]        = useState(false);
  const [isbdOpen,         setIsbdOpen]         = useState(false);
  const [contentsOpen,     setContentsOpen]     = useState(false);
  const [inventoryOpen,    setInventoryOpen]    = useState(false);
  const [fulltextsOpen,    setFulltextsOpen]    = useState(false);
  const [webViews,         setWebViews]         = useState(null);
  const [fulltextViews,    setFulltextViews]    = useState(null);
  const [showOrderWarning, setShowOrderWarning] = useState(false);
  // Уся логіка модалки замовлення (вибір пункту видачі, борг, дубль, поле 56,
  // анімація закриття, submit) живе в спільному <OrderModal> (OrderModal.js) —
  // тут лишається тільки прапорець монтування.
  const [showOrderPanel,   setShowOrderPanel]   = useState(false);
  const [copied,           setCopied]           = useState(false);
  const [copyFailed,       setCopyFailed]       = useState(false);
  const [bibFormat,      setBibFormat]     = useState('isbd');
  const [unimarcData,    setUnimarcData]   = useState(null);
  const [unimarcLoading, setUnimarcLoading] = useState(false);
  const [unimarcError,   setUnimarcError]  = useState(null);

  const coverStickyRef = useRef(null);
  useEffect(() => {
    if (window.innerWidth < 992) return;
    const el = coverStickyRef.current;
    if (!el) return;

    const STICK_TOP = 94;
    const parentTop = el.parentElement.getBoundingClientRect().top + window.scrollY;
    const stickyStart = parentTop - STICK_TOP;

    let current = window.scrollY;
    let target = current;
    let rafId;

    const onScroll = () => { target = window.scrollY; };
    const onResize = () => { if (window.innerWidth < 992) el.style.transform = ''; };

    const tick = () => {
      current += (target - current) * 0.1;
      const maxTranslate = el.parentElement.offsetHeight - el.offsetHeight;
      const translateY = Math.min(maxTranslate, Math.max(0, current - stickyStart));
      el.style.transform = `translateY(${translateY}px)`;
      rafId = requestAnimationFrame(tick);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    rafId = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafId);
      if (el) el.style.transform = '';
    };
  }, [mfn, db]);

  const isGuest        = userMode === 'guest';
  const effectiveLogin = user?.login || (isGuest ? GUEST_LOGIN : null);
  // Ідентифікатор читача (поле 30 RI=) для журналу дій — тільки зареєстрований
  // читач; гість не має власного field 30 (GUEST_LOGIN — лише службовий ключ
  // локального сховища, не справжній логін).
  const readerLoginForLog = (!isGuest && user?.login) ? user.login : null;

  // ── Завантаження запису + лічильників ──────────────────
  useEffect(() => {
    setLoading(true);
    setError('');
    setCoverError(false);
    setAnnotOpen(false);
setBibFormat('isbd');
setUnimarcData(null);
setUnimarcError(null);

    fetch(`${API_URL}/record.php?mfn=${mfn}&db=${db}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setRecord(data);
          if (effectiveLogin) {
            getBookmarks(effectiveLogin, user)
              .then(bm => setBookmarked(bm.some(b => b.mfn === mfn && b.db === db)))
              .catch(() => setBookmarked(false));
          }
          // Лічильники: перегляди і звернення до повного тексту
          fetch(`${API_URL}/popular.php?action=top&limit=200`)
            .then(r => r.json())
            .then(pop => {
              const key   = `${db.toUpperCase()}:${mfn}`;
              const found = pop.records?.find(r => `${r.db}:${r.mfn}` === key);
              setWebViews(found?.views ?? 0);
              setFulltextViews(found?.fulltext_views ?? 0);
            })
            .catch(() => { setWebViews(0); setFulltextViews(0); });
        }
      })
      .catch(() => setError(t(lang, 'book_error')))
      .finally(() => setLoading(false));
  }, [mfn, db, user, effectiveLogin, isGuest]); // eslint-disable-line react-hooks/exhaustive-deps

const loadUnimarc = useCallback(() => {
  if (unimarcData !== null || unimarcLoading) return;
  setUnimarcLoading(true);
  setUnimarcError(null);
  if (trackEvent) trackEvent('unimarc', { db_name: db, mfn, login: readerLoginForLog });
  fetch(`${API_URL}/unimarc.php?mfn=${mfn}&db=${db}`)
    .then(r => r.json())
    .then(d => {
      if (d.error) throw new Error(d.error);
      setUnimarcData(d.fields || []);
    })
    .catch(err => {
      setUnimarcError(err.message || 'Помилка завантаження UNIMARC');
      setUnimarcData([]);
    })
    .finally(() => setUnimarcLoading(false));
}, [mfn, db, unimarcData, unimarcLoading, trackEvent, readerLoginForLog]);

  // ── Закладки ──────────────────────────────────────────
const toggleBookmark = () => {
    if (!effectiveLogin || !record) return;
    if (bookmarked) {
      removeBookmark(effectiveLogin, user, db, mfn);
      if (isGuest && trackEvent) trackEvent('bookmark', { detail: 'remove', db_name: db, mfn });
    } else {
      addBookmark(effectiveLogin, user, {
        mfn,
        db,
        title:     record.title,
        author:    record.authors?.[0] || '',
        year:      record.year || '',
        cover_url: record.cover_url || null,
        addedAt:   new Date().toLocaleDateString('uk-UA'),
      });
      if (isGuest && trackEvent) trackEvent('bookmark', { detail: 'add', db_name: db, mfn });
    }
    setBookmarked(!bookmarked);
  };

  // ── Допоміжні обчислення ──────────────────────────────
  const freeCount  = record?.inventory?.filter(i => i.status_code === '0').length ?? 0;
  const totalCount = record?.inventory?.length ?? 0;

  const userHasBook = !!(
    issuedBooks?.length &&
    issuedBooks.some(b => b.mfn === mfn && b.db === db)
  );

  // Читач-боржник: замовляти новий примірник не можна, лише якщо хоча б
  // одне видання на руках ПРОСТРОЧЕНЕ (is_overdue, той самий розрахунок,
  // що й у cabinet.php). Продовжена, але не прострочена література —
  // не заважає (сесія 2026-08-10/5 — уточнення користувача поверх
  // початкової реалізації сесії 2026-08-10/3, яка блокувала за будь-якою
  // наявністю на руках незалежно від строку).
  const hasDebt = !!(issuedBooks && issuedBooks.some(b => b.is_overdue));

  // Читач вже має активне (необроблене) замовлення саме цієї книги —
  // підсвічуємо кнопку "Замовити" оранжевим, щоб було видно ще до кліку,
  // що видання вже переглядалось і замовлялось. Список приходить з App.js
  // (prefetch раз на сесію, order.php?action=list) — тут лише звіряємо
  // db+mfn локально, без нового звернення до сервера.
  const hasActiveOrder = !!(
    activeOrders?.length &&
    activeOrders.some(o => o.mfn === mfn && o.db === db)
  );

  // Порожнє поле 56 у RDR (типово для щойно самостійно зареєстрованого
  // читача, поки бібліотекар не призначив пункти видачі) — order.php і так
  // блокує замовлення (restricted: true) при кліку, тут лише проактивно
  // фарбуємо кнопку сірою ще до нього. Значення приходить з login.php,
  // актуальне на момент входу, без додаткового запиту.
  const orderAccountRestricted = user?.mode === 'user' && !!user?.order_restricted;

const hideOrderBlock = totalCount === 0 && record?.fulltexts?.length > 0;

  const orderBtnLabel = (() => {
    if (userHasBook)      return { text: t(lang, 'book_on_hand'),              icon: 'fa-solid fa-check-circle',  variant: 'warning' };
    if (totalCount === 0) return { text: t(lang, 'book_order'),                    icon: 'fa-solid fa-cart-shopping', variant: 'busy'  };
    if (freeCount === 0)  return { text: t(lang, 'book_all_issued'),                  icon: 'fa-solid fa-clock',         variant: 'busy'    };
    if (orderAccountRestricted) return { text: t(lang, 'book_order'),               icon: 'fa-solid fa-cart-shopping', variant: 'busy'    };
    const normalVariant = hasDebt ? 'debt' : hasActiveOrder ? 'duplicate' : 'normal';
    if (totalCount > 1)   return { text: `${t(lang, 'book_order')} · ${t(lang, 'book_free_of')}: ${freeCount}`, icon: 'fa-solid fa-cart-shopping', variant: normalVariant };
    return                       { text: t(lang, 'book_order'),                    icon: 'fa-solid fa-cart-shopping', variant: normalVariant };
  })();

  // Борг відомий одразу (issuedBooks) — передаємо в <OrderModal initialDebt>,
  // щоб зберегти миттєвий відгук без запиту до order.php (як було раніше).
  const handleOrderClick = () => {
    if (userHasBook) { setShowOrderWarning(true); return; }
    setShowOrderPanel(true);
  };
  // Журнал поширення (сесія 2026-08-12) — канал (viber/telegram/facebook/
  // messenger/email/copy) пишеться в events.detail.
  const handleShare = useCallback((channel) => {
    if (trackEvent) trackEvent('share', { db_name: db, mfn, detail: channel, login: readerLoginForLog });
  }, [trackEvent, db, mfn, readerLoginForLog]);

  // copyText() (clipboard.js) сам переходить на фолбек без Clipboard API;
  // якщо і він не спрацював — показуємо помітну помилку, а не тишу.
  const handleCopyLink = useCallback(() => {
    const onSuccess = () => { setCopied(true); setCopyFailed(false); setTimeout(() => setCopied(false), 2000); };
    const onFail    = () => { setCopyFailed(true); setTimeout(() => setCopyFailed(false), 3000); };
    copyText(window.location.href).then(ok => (ok ? onSuccess() : onFail()));
  }, []);

  const shareUrl   = window.location.href;
  const shareTitle = record?.title
    ? record.title + (record.authors?.[0] ? ` / ${record.authors[0]}` : '')
    : '';

  // ── Відстеження звернень до повного тексту ────────────
const trackFulltext = useCallback((source) => {
    if (mfn <= 0 || !db) return;
    if (trackEvent) trackEvent('fulltext', { db_name: db, mfn, detail: source, login: readerLoginForLog });
    fetch(`${API_URL}/popular.php?action=track_fulltext&mfn=${mfn}&db=${encodeURIComponent(db)}`)
      .then(r => r.json())
      .then(d => {
        if (typeof d.fulltext_views === 'number') setFulltextViews(d.fulltext_views);
      })
      .catch(() => {});
  }, [mfn, db, trackEvent, readerLoginForLog]);

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════
  return (
    <div className="bfv-page">

      {/* ── Loading ── */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted small">{t(lang, 'book_loading')}</p>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="container-lg py-4">
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <i className="fa-solid fa-triangle-exclamation flex-shrink-0" />{error}
          </Alert>
          <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
            <i className="fa-solid fa-arrow-left me-1" />{t(lang, 'back')}
          </button>
        </div>
      )}

      {/* ── Main content ── */}
      {record && !loading && (
        <div className="bfv-container">

{/* Кнопка «Назад» + поширення — мобіл */}
          <BackShareRow wrapClass="bfv-back-btn-mobile" onBack={onBack} backLabel={backLabel} lang={lang}
            shareUrl={shareUrl} shareTitle={shareTitle} copied={copied} copyFailed={copyFailed} onCopyLink={handleCopyLink} onShare={handleShare} />

          {/* ══════════════════════
              ЛІВА КОЛОНКА — обкладинка
          ══════════════════════ */}
          <div className="bfv-cover-col">
            <div ref={coverStickyRef} className="bfv-cover-sticky bfv-cover-block">

              {/* Обкладинка */}
              {record.cover_url && !coverError ? (
                <div className="bfv-cover-wrap">
                  <img
                    src={record.cover_url}
                    alt={`Обкладинка: ${record.title}`}
                    className="bfv-cover-img"
                    onError={() => setCoverError(true)}
                  />
                </div>
              ) : (
                <div className="bfv-cover-placeholder cover-art-host">
                  <CoverArt large db={db}
                    type={record.material_type || (record.journal_code ? '02' : record.vyd_type)} />
                </div>
              )}

              {/* Бейдж наявності */}
              {record.inventory?.length > 0 && (
                <div className="bfv-availability-summary">
                  {freeCount > 0 ? (
                    <span className="bfv-avail-badge bfv-avail-free">
                      <i className="fa-solid fa-circle-check me-1" />
                      {t(lang, 'book_free_of')}: {freeCount} {t(lang, 'book_of')} {record.inventory.length}
                    </span>
                  ) : (
                    <span className="bfv-avail-badge bfv-avail-busy">
                      <i className="fa-solid fa-clock me-1" />
                      {t(lang, 'book_all_issued_badge')}
                    </span>
                  )}
                </div>
              )}

              {/* Кнопка «Замовити» або плашка гостя — схована для е-ресурсів з повними текстами */}
              {!hideOrderBlock && (isGuest ? (
                <div className="bfv-guest-order-notice">
                  <i className="fa-solid fa-lock" />
                  {t(lang, 'book_login_order')}
                  <GuestAuthButtons onGoLogin={onGoLogin} lang={lang} />
                </div>
              ) : (
                <>
                  <button
                    className={`bfv-order-btn${
                      orderBtnLabel.variant === 'busy'      ? ' bfv-order-btn--busy'      :
                      orderBtnLabel.variant === 'warning'   ? ' bfv-order-btn--warning'   :
                      orderBtnLabel.variant === 'debt'      ? ' bfv-order-btn--debt'      :
                      orderBtnLabel.variant === 'duplicate' ? ' bfv-order-btn--duplicate' : ''
                    }`}
                    onClick={handleOrderClick}
                    disabled={freeCount === 0 && !userHasBook}
                  >
                    <i className={`${orderBtnLabel.icon} me-2`} />{orderBtnLabel.text}
                  </button>

                  {showOrderWarning && (
                    <div className="bfv-order-warning">
                      <i className="fa-solid fa-triangle-exclamation me-2" />
                      <strong>Увага!</strong> Це видання вже є у вас на руках.
                      Якщо хочете замовити інший примірник —{' '}
                      <button
                        className="bfv-order-warning-confirm"
                        onClick={() => {
                          setShowOrderWarning(false);
                          setShowOrderPanel(true);
                        }}
                      >підтвердити</button>
                      {' або '}
                      <button
                        className="bfv-order-warning-cancel"
                        onClick={() => setShowOrderWarning(false)}
                      >скасувати</button>.
                    </div>
                  )}

                  {showOrderPanel && (
                    <OrderModal
                      db={db}
                      mfn={mfn}
                      user={user}
                      lang={lang}
                      trackEvent={trackEvent}
                      initialDebt={hasDebt}
                      onOrderPlaced={onOrderPlaced}
                      onOpenCabinetSection={onOpenCabinetSection}
                      onClose={() => setShowOrderPanel(false)}
                    />
                  )}
                </>
              ))}
{/* Закладка — під кнопкою замовлення */}
              {effectiveLogin && (
                <button
                  className={`bfv-action-btn${bookmarked ? ' bfv-action-btn--active' : ''}`}
                  onClick={toggleBookmark}>
                  <i className={`${bookmarked ? 'fa-solid' : 'fa-regular'} fa-bookmark me-2`} />
                  {bookmarked ? t(lang, 'book_in_bookmarks') : t(lang, 'book_to_bookmarks')}
                </button>
              )}

{/* DOI видання */}
              {record.doi && (
                <a
                  href={_safeExtUrl(record.doi)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bfv-doi-btn">
                  <i className="fa-solid fa-fingerprint me-2" />
                  {t(lang, 'book_doi_btn')}
                </a>
              )}
            </div>
            </div>

          {/* ══════════════════════
              ПРАВА КОЛОНКА — інфо
          ══════════════════════ */}
          <div className="bfv-info-col">

            <div className="bfv-section-block">
{/* Назад + поширення — десктоп */}
            <BackShareRow wrapClass="bfv-back-btn-desktop" onBack={onBack} backLabel={backLabel} lang={lang}
              shareUrl={shareUrl} shareTitle={shareTitle} copied={copied} copyFailed={copyFailed} onCopyLink={handleCopyLink} onShare={handleShare} />

{/* Заголовок */}
            <h1 className="bfv-title">
              {record.title}
              {(record.ser_designation || record.vol_title || record.issue_num) && (
                <span className="bfv-title-desig">
                  {record.ser_designation
                    ? `. ${record.ser_designation}`
                    : <>
                        {record.vol_title ? (/^\d/.test(record.vol_title) ? `. Т.\u00A0${record.vol_title}` : `. ${record.vol_title}`) : ''}
                        {record.issue_num ? `. №\u00A0${record.issue_num}` : ''}
                      </>
                  }
                </span>
              )}
              {record.subtitle && (
                <span className="bfv-title-subtitle"> : {record.subtitle}</span>
              )}
              {record.journal_year && record.issue_label && (
                <span className="bfv-title-subtitle bfv-title-subtitle--issue"> ({record.journal_year}, № {record.issue_label})</span>
              )}
            </h1>
            {record.series && (
              <p className="bfv-series-line">
                {record.series}{record.series_vol ? ` ; ${record.series_vol}` : ''}
              </p>
            )}
            {record.journal_code && onOpenJournalSummary && (
              <button
                type="button"
                className="bfv-journal-summary-link"
                onClick={() => onOpenJournalSummary(record.journal_code, db, record.journal_year)}
              >
                <i className="fa-solid fa-book-open-reader me-1" />
                {t(lang, 'book_journal_summary_link')}
              </button>
            )}

            {/* Автори — як пілюлі */}
            {record.authors?.length > 0 && (
              <div className="bfv-authors">
{record.authors.map((a, i) => (
  <button
    key={i}
    className={`bfv-author-pill${onAuthorSearch ? ' bfv-author-pill--clickable' : ''}`}
    onClick={() => onAuthorSearch && onAuthorSearch(a)}
    title={onAuthorSearch ? `Знайти всі видання автора: ${a}` : undefined}
  >
    <i className="fa-solid fa-user me-1" />{a}
    {onAuthorSearch && (
      <i className="fa-solid fa-magnifying-glass ms-1" style={{ fontSize: '9px', opacity: 0.55 }} />
    )}
  </button>
))}
              </div>
            )}

            {/* ── Статистика ── */}
            <div className="bfv-stats-row">
              {/* Книговидачі — показуємо завжди (навіть 0) */}
              <div className="bfv-stat-item" title="Кількість книговидач з бази">
                <i className="fa-solid fa-book-open bfv-stat-icon" />
                <span className="bfv-stat-label">{t(lang, 'book_loans')}</span>
                <strong className="bfv-stat-val">{record.loan_count ?? 0}</strong>
              </div>
              {/* Перегляди у веб-каталозі */}
              {(webViews ?? 0) > 0 && (
                <div className="bfv-stat-item" title="Кількість переглядів у веб-каталозі">
                  <i className="fa-regular fa-eye bfv-stat-icon" />
                  <span className="bfv-stat-label">{t(lang, 'book_web_views')}</span>
                  <strong className="bfv-stat-val">{webViews}</strong>
                </div>
              )}
              {/* Звернення до повного тексту */}
              {(fulltextViews ?? 0) > 0 && record.fulltexts?.length > 0 && (
                <div className="bfv-stat-item" title="Кількість звернень до повного тексту">
                  <i className="fa-solid fa-file-lines bfv-stat-icon" />
                  <span className="bfv-stat-label">{t(lang, 'book_ft_views')}</span>
                  <strong className="bfv-stat-val">{fulltextViews}</strong>
                </div>
              )}
            </div>
            </div>{/* /bfv-section-block (назад + заголовок + автори + статистика) */}


            {/* ── Бібліографічні дані ── */}
            <div className="bfv-biblio-card">
              <h3 className="bfv-section-title">
                <i className="fa-solid fa-circle-info me-2" />{t(lang, 'book_main_info')}
              </h3>
              <div className="bfv-info-table">
  {record.analytics_source ? (
    /* ── Стаття (KST): дані з поля 463 ── */
    <>
      <InfoRow icon="fa-regular fa-newspaper" label={t(lang, 'book_source')}
        value={record.analytics_source.title} />
      <InfoRow icon="fa-regular fa-calendar"    label={t(lang, 'book_year')}   value={record.year}   />
      <InfoRow icon="fa-solid fa-layer-group"   label={t(lang, 'book_volume')} value={record.analytics_source.vol}   />
      <InfoRow icon="fa-solid fa-hashtag"         label={t(lang, 'book_issue')}  value={record.analytics_source.issue} />
      <InfoRow icon="fa-regular fa-file" label={t(lang, 'book_pages')}  value={record.pages}  />
      <InfoRow icon="fa-solid fa-language" label={t(lang, 'book_language')}
  value={
    record.all_languages?.length > 0
      ? record.all_languages.map(l => langName(l)).filter(Boolean).join(', ')
      : langName(record.language)
  } />
      <UdcBadgeRow record={record} lang={lang} onUdcSearch={onUdcSearch} />
      <InfoRow icon="fa-solid fa-tags"    label={t(lang, 'book_bbk')}    value={record.bbk}    />
    </>
  ) : (
    /* ── Книга / автореферат (KV, KAD) ── */
    <>
      <InfoRow icon="fa-solid fa-building"     label={t(lang, 'book_publisher')}
        value={record.place ? `${record.place}, ${record.publisher || '[б. в.]'}` : (record.publisher || null)} />
      <InfoRow icon="fa-regular fa-calendar"    label={t(lang, 'book_year')}    value={record.year}     />
      <InfoRow icon="fa-solid fa-book-open"     label={t(lang, 'book_edition')} value={record.edition}  />
      <InfoRow icon="fa-regular fa-file" label={t(lang, 'book_pages')}   value={record.pages}    />
      <InfoRow icon="fa-solid fa-layer-group"   label={t(lang, 'book_series')}  value={record.series}   />
      <InfoRow icon="fa-solid fa-language" label={t(lang, 'book_language')}
  value={
    record.all_languages?.length > 0
      ? record.all_languages.map(l => langName(l)).filter(Boolean).join(', ')
      : langName(record.language)
  } />
      <InfoRow icon="fa-solid fa-barcode"     label={t(lang, 'book_isbn')}    value={record.isbn}     />
      <UdcBadgeRow record={record} lang={lang} onUdcSearch={onUdcSearch} />
      <InfoRow icon="fa-solid fa-tags"    label={t(lang, 'book_bbk')}     value={record.bbk}      />
    </>
  )}
</div>
            </div>

            {/* ── Єдиний блок: MARC + Примірники + Зміст + Ключові слова + Анотація + Дії ── */}
<div className="bfv-section-block bfv-section-block--list">

            {/* ── Повний бібліографічний опис (акордеон) ── */}
              <Accordion flush activeKey={isbdOpen ? '0' : null} onSelect={k => setIsbdOpen(!!k)}>
                <Accordion.Item eventKey="0">
<Accordion.Header>
  <i className="fa-solid fa-rectangle-list me-2" />
  {t(lang, 'book_full_desc')}
  <i className="fa-solid fa-chevron-down bfv-acc-chevron" />
</Accordion.Header>
<Accordion.Body className="p-0">
  <div className="bfv-marc-tabs">
    <button
      className={`bfv-marc-tab${bibFormat === 'isbd' ? ' bfv-marc-tab--active' : ''}`}
      onClick={() => setBibFormat('isbd')}
    >{t(lang, 'book_format_isbd')}</button>
    <button
      className={`bfv-marc-tab${bibFormat === 'unimarc' ? ' bfv-marc-tab--active' : ''}`}
      onClick={() => { setBibFormat('unimarc'); loadUnimarc(); }}
    >{t(lang, 'book_format_unimarc')}</button>
  </div>
  {bibFormat === 'isbd'
    ? <IsbdDescription record={record} db={db} onRubricSearch={onKeywordSearch} />
    : <UnimarcView fields={unimarcData} loading={unimarcLoading} error={unimarcError} />
  }
</Accordion.Body>
                </Accordion.Item>
              </Accordion>

{/* ── Примірники (акордеон) ── */}
{record.inventory?.length > 0 && (
    <Accordion flush activeKey={inventoryOpen ? '0' : null} onSelect={k => setInventoryOpen(!!k)}>
      <Accordion.Item eventKey="0">
<Accordion.Header>
  <i className="fa-solid fa-box-archive me-2" />
  {t(lang, 'book_availability')}
  <span className="bfv-isbd-std-badge ms-2">
    {record.inventory.length} {t(lang, 'book_copies')}
    {freeCount > 0 && ` · ${t(lang, 'book_free_of')}: ${freeCount}`}
  </span>
  <i className="fa-solid fa-chevron-down bfv-acc-chevron" />
</Accordion.Header>
        <Accordion.Body className="p-0">
{(() => {
            const inv = record.inventory;
            const grp = arr => arr.reduce((acc, item) => {
              const k = item.place || 'Основний фонд';
              acc[k] = (acc[k] || 0) + 1;
              return acc;
            }, {});
            const allGrp  = grp(inv);
            const freeGrp = grp(inv.filter(i => i.status_code === '0'));
            const places  = Object.entries(allGrp).map(([place, total]) => ({
              place, total, free: freeGrp[place] || 0,
            }));
            return (
              <div className="bfv-inv-grid">
                {places.map(({ place, total, free }) => (
<div key={place} className="bfv-inv-card">
                    <div className="bfv-inv-card-place">{place}</div>
                    <div className="bfv-inv-card-body">
                      <div className="bfv-inv-card-row">
                        <span className="bfv-inv-card-label">Всього:</span>
                        <span className="bfv-inv-card-count">{total}</span>
                      </div>
                      <div className="bfv-inv-card-row">
                        <span className="bfv-inv-card-label">Вільних:</span>
                        <span className={free > 0 ? 'bfv-inv-card-count--ok' : 'bfv-inv-card-count--no'}>{free}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
)}

{/* ── Зміст / Оглавлення ── */}
{record.contents?.length > 0 && (
    <Accordion flush activeKey={contentsOpen ? '0' : null} onSelect={k => setContentsOpen(!!k)}>
      <Accordion.Item eventKey="0">
<Accordion.Header>
  <i className="fa-solid fa-list-ul me-2" />
  Зміст
  <span className="bfv-isbd-std-badge ms-2">{record.contents.length} позицій</span>
  <i className="fa-solid fa-chevron-down bfv-acc-chevron" />
</Accordion.Header>
        <Accordion.Body className="p-0">
          <div className={`bfv-contents-list${record.contents.length > 10 ? ' bfv-contents-list--scrollable' : ''}`}>
            {record.contents.map((item, i) => (
              <div key={i} className="bfv-contents-item">
                <span className="bfv-contents-num">{i + 1}.</span>
                {item.url ? (
  <a
  href={_safeExtUrl(item.url)}
  target="_blank"
  rel="noopener noreferrer"
  className="bfv-contents-body bfv-contents-body--link">
  {item.author && <span className="bfv-contents-author">{item.author}</span>}
  {item.title && <span className="bfv-contents-title">{item.title}</span>}
  {item.title_ua && <span className="bfv-contents-resp"> = {item.title_ua}</span>}
  {item.resp && <span className="bfv-contents-resp"> / {item.resp}</span>}
  {item.pages && <span className="bfv-contents-pages">С. {item.pages}</span>}
</a>
) : (
<div className="bfv-contents-body">
  {item.author && (
    <button
      className={`bfv-rubric-tag bfv-contents-author${onAuthorSearch ? ' bfv-rubric-clickable' : ''}`}
      onClick={() => onAuthorSearch && onAuthorSearch(item.author)}
      title={onAuthorSearch ? `Знайти всі твори: ${item.author}` : undefined}>
      {item.author}
    </button>
  )}
  {item.title && <span className="bfv-contents-title">{item.title}</span>}
  {item.title_ua && <span className="bfv-contents-resp"> = {item.title_ua}</span>}
  {item.resp && <span className="bfv-contents-resp"> / {item.resp}</span>}
  {item.pages && <span className="bfv-contents-pages">С. {item.pages}</span>}
</div>
)}
              </div>
            ))}
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
)}


            {/* ── Анотація ── */}
            {record.annotation && (
    <Accordion flush activeKey={annotOpen ? '0' : null} onSelect={k => setAnnotOpen(!!k)}>
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          <i className="fa-solid fa-align-left me-2" />
          {t(lang, 'book_annotation')}
          <i className="fa-solid fa-chevron-down bfv-acc-chevron" />
        </Accordion.Header>
        <Accordion.Body>
          <p className="bfv-annotation mb-0">{record.annotation}</p>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
)}

{/* ── Повні тексти видання (аккордеон) ── */}
{record.fulltexts?.length > 0 && (
  <Accordion flush activeKey={fulltextsOpen ? '0' : null} onSelect={k => setFulltextsOpen(!!k)}>
    <Accordion.Item eventKey="0">
      <Accordion.Header>
        <i className="fa-solid fa-file-lines me-2" />
        {t(lang, 'book_fulltexts')}
        <span className="bfv-isbd-std-badge ms-2">{record.fulltexts.length}</span>
        <i className="fa-solid fa-chevron-down bfv-acc-chevron" />
      </Accordion.Header>
      <Accordion.Body>
        <div className="d-flex flex-column gap-2 bfv-fulltexts-list">
          {isGuest ? (
            // Гостю недоступний жоден повний текст (ні внутрішній, ні зовнішній) —
            // політика бібліотеки, одна спільна плашка замість посилань/кнопок
            <div className="bfv-guest-ft-notice">
              <i className="fa-solid fa-lock" />
              {t(lang, 'book_login_fulltext')}
              <GuestAuthButtons onGoLogin={onGoLogin} lang={lang} />
            </div>
          ) : record.fulltexts.map((ft, i) => {
            const openedInfo = () => ({
              mfn, db,
              title:  record.title || '',
              author: record.authors?.[0] || record.main_author || '',
              year:   record.year || '',
              url:    ft.url,
              label:  ft.label || '',
              source: ft.source,
              accessedAt: new Date().toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            });

            // Внутрішні тексти — читач: тікет-цикл (fulltext.php вимагає
            // одноразовий квиток, звичайний href без нього поверне 401)
            if (ft.source === 'internal') {
              return (
                <button key={i}
                  type="button"
                  // Тимчасово вимкнено (2026-11-03): внутрішні повні тексти не відкриваються.
                  // Щоб увімкнути — прибрати disabled; стиль — .bfv-action-btn--ft-int:disabled
                  disabled
                  onClick={() => {
                    const win = window.open('', '_blank'); // синхронно — інакше браузер блокує popup
                    if (!win) return;
                    readerFetch(`${API_URL}/fulltext.php?action=ticket`, user?.reader_token)
                      .then(r => r.json())
                      .then(d => {
                        if (!d.ticket) throw new Error('no ticket');
                        win.location.href = `${ft.url}&ticket=${d.ticket}`;
                        trackFulltext('internal');
                        if (onFulltextOpen) onFulltextOpen(openedInfo());
                      })
                      .catch(() => { if (win && !win.closed) win.close(); });
                  }}
                  className="bfv-action-btn bfv-action-btn--ft-int">
                  <i className="fa-solid fa-file-pdf me-2" />
                  {t(lang, 'ft_int_label')}
                </button>
              );
            }

            // Зовнішні тексти — доступні всім, без змін
            return (
              <a key={i}
                href={_safeExtUrl(ft.url)}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackFulltext('external');
                  if (onFulltextOpen) onFulltextOpen(openedInfo());
                }}
                className="bfv-action-btn bfv-action-btn--ft-ext">
                <i className={`fa-solid ${getFtIcon(ft.label)} me-2`} />
                {stripHtml(ft.label) || t(lang, 'ft_ext_label')}
              </a>
            );
          })}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  </Accordion>
)}

            {/* ── Ключові слова (кліковані) ── */}
            {record.keywords?.length > 0 && (
              <div className="bfv-kw-row">
                <div className="bfv-kw-row-header">
                  <i className="fa-solid fa-tags me-2" />{t(lang, 'book_keywords')}
                </div>
                <div className="bfv-keywords">
                  {record.keywords.map((kw, i) => (
                    <button
                      key={i}
                      className={`bfv-keyword-tag${onKeywordSearch ? ' bfv-keyword-clickable' : ''}`}
                      onClick={() => onKeywordSearch && onKeywordSearch(kw)}
                      title={onKeywordSearch ? `Знайти всі видання за: ${kw}` : undefined}>
                      {onKeywordSearch && (
                        <i className="fa-solid fa-magnifying-glass me-1"
                          style={{ fontSize: '10px', opacity: 0.6 }} />
                      )}
                      {kw}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>{/* /bfv-section-block--list */}
          </div>
        </div>
      )}
    </div>
  );
}

export default BookFullView;